import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, X, Loader2, CheckCircle2, AlertCircle, ExternalLink, StopCircle } from 'lucide-react'
import { Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { useScanPanel } from '../contexts/ScanPanelContext'
import { useNavigate } from 'react-router-dom'
import { abortScan, getFreshAccessToken } from '../../../lib/api/api'
import { PDF_DOCUMENT_OPTIONS } from '../../../lib/pdfjs'
import { isSameOriginApiPath, pdfPreviewSource, PdfPreviewSource } from '../previewSource'

export default function ScanPanel() {
  const { t } = useTranslation()
  const {
    isOpen,
    scans,
    currentIndex,
    current,
    goToNext,
    goToPrev,
    closePanel,
  } = useScanPanel()
  const [numPages, setNumPages] = useState<number | null>(null)
  const [aborting, setAborting] = useState(false)
  // react-pdf reloads when `file` changes identity: one object per preview.
  const [pdfFile, setPdfFile] = useState<PdfPreviewSource | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)
  const navigate = useNavigate()
  const previewUrl = isOpen ? current?.preview_url : undefined

  useEffect(() => {
    let cancelled = false
    setPdfFile(null)
    setNumPages(null)
    setPreviewFailed(!isSameOriginApiPath(previewUrl))
    if (!isSameOriginApiPath(previewUrl)) return
    ;(async () => {
      const source = pdfPreviewSource(previewUrl, await getFreshAccessToken())
      if (cancelled) return
      setPdfFile(source)
      setPreviewFailed(source === null)
    })()
    return () => {
      cancelled = true
    }
  }, [previewUrl])

  const handleAbort = async () => {
    if (!current || aborting) return
    setAborting(true)
    try {
      await abortScan(current.scan_id)
    } catch (e) {
      console.warn('abort failed', e)
    } finally {
      setAborting(false)
    }
  }

  if (!isOpen || !current) return null

  const total = scans.length
  const canPrev = currentIndex > 0
  const canNext = currentIndex < total - 1

  const visionGrid = (() => {
    const total = current.vision_total_pages ?? 0
    if (!total) return null
    const pages = current.vision_pages ?? {}
    const doneCount = Object.values(pages).filter((s) => s === 'done').length
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{t('scanPanel.visionParallel')}</span>
          <span>{doneCount}/{total}</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {Array.from({ length: total }, (_, i) => i + 1).map((n) => {
            const state = pages[n] ?? 'pending'
            const base = 'inline-flex items-center justify-center h-6 min-w-[1.75rem] px-1 rounded text-[11px] font-medium tabular-nums border transition-colors duration-150'
            const cls =
              state === 'done'
                ? 'bg-green-500 text-white border-green-500'
                : state === 'processing'
                ? 'bg-yellow-400 text-gray-900 border-yellow-400 animate-pulse'
                : 'bg-transparent text-gray-500 border-gray-300 dark:border-gray-600'
            return (
              <span key={n} className={`${base} ${cls}`}>
                {n}
              </span>
            )
          })}
        </div>
      </div>
    )
  })()

  const statusLabel = (() => {
    switch (current.status) {
      case 'received':
      case 'processing':
        return (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 min-w-0">
                <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                <span className="truncate">{current.stage_label ?? t('scanPanel.processing')}</span>
              </div>
              <button
                type="button"
                onClick={handleAbort}
                disabled={aborting}
                className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                title={t('scanPanel.abortProcessing')}
              >
                <StopCircle className="w-3.5 h-3.5" />
                {aborting ? t('scanPanel.aborting') : t('scanPanel.abort')}
              </button>
            </div>
            {visionGrid}
            {(current.chunks_total != null || current.total_pages != null) && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {current.chunks_total != null && current.chunks_embedded != null && (
                  <>{t('scanPanel.chunk')} {current.chunks_embedded}/{current.chunks_total}</>
                )}
                {current.chunks_total != null && current.chunks_embedded != null && current.total_pages != null && (
                  <> — </>
                )}
                {current.total_pages != null && (
                  <>{current.total_pages} {t('scanPanel.pages')}</>
                )}
              </div>
            )}
          </>
        )
      case 'complete':
        return (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <CheckCircle2 className="w-4 h-4" />
            <span>{t('scanPanel.ready', { title: current.title || current.file_name })}</span>
          </div>
        )
      case 'error':
        return (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4" />
            <span>{t('scanPanel.error', { error: current.error || t('scanPanel.processingFailed') })}</span>
          </div>
        )
    }
  })()

  const goToDoc = () => {
    if (current.doc_id) {
      navigate(`/documents?highlight=${current.doc_id}`)
      closePanel()
    }
  }

  return (
    <>
      {/* Backdrop — starts below navbar (h-14 = 3.5rem) so the resource
          monitor stays visible and interactive during processing. */}
      <div
        className="fixed top-14 right-0 bottom-0 left-0 bg-black/30 z-40"
        onClick={closePanel}
        aria-hidden="true"
      />

      {/* Slide-over panel — offset by navbar height for the same reason. */}
      <aside
        className="fixed right-0 top-14 h-[calc(100vh-3.5rem)] w-[480px] max-w-[40vw] bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col transition-transform duration-200"
        role="dialog"
        aria-label={t('scanPanel.previewTitle')}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goToPrev}
              disabled={!canPrev}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={t('scanPanel.prevDocument')}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('scanPanel.documentOf', { current: currentIndex + 1, total })}
            </span>
            <button
              type="button"
              onClick={goToNext}
              disabled={!canNext}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label={t('scanPanel.nextDocument')}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <button
            type="button"
            onClick={closePanel}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* PDF preview */}
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-950 flex items-start justify-center p-4 relative">
          {pdfFile ? (
            <Document
              file={pdfFile}
              options={PDF_DOCUMENT_OPTIONS}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={<Loader2 className="w-8 h-8 animate-spin text-gray-400 mt-12" />}
              error={
                <div className="text-sm text-gray-500 mt-12">
                  {t('scanPanel.previewUnavailable')}
                </div>
              }
            >
              <Page
                pageNumber={1}
                width={400}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          ) : previewFailed ? (
            <div className="text-sm text-gray-500 mt-12">
              {t('scanPanel.previewUnavailable')}
            </div>
          ) : (
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mt-12" />
          )}
          {numPages && numPages > 1 && (
            <div className="absolute bottom-4 right-4 text-xs text-gray-500 bg-white/80 dark:bg-gray-800/80 px-2 py-1 rounded">
              {t('scanPanel.pageOf', { total: numPages })}
            </div>
          )}
        </div>

        {/* Status footer */}
        <footer className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 space-y-2">
          {statusLabel}
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {current.file_name}
          </div>
          {current.status === 'complete' && current.doc_id && (
            <button
              type="button"
              onClick={goToDoc}
              className="w-full mt-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded flex items-center justify-center gap-2"
            >
              {t('scanPanel.goToDocument')}
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </footer>
      </aside>
    </>
  )
}
